import type { ApexOptions } from 'apexcharts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTheme } from '../../context/ThemeContext';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  BillingSummary,
  formatCompactUsd,
  formatMoney,
  formatPercent,
  numberValue,
  titleCase,
} from './billingAdminShared';

const BILLING_REQUEST_TIMEOUT_MS = 30_000;

function KpiCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="app-panel rounded-[22px] border border-[rgb(var(--app-border)/0.65)] p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[rgb(var(--app-muted))]">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold text-[rgb(var(--app-text))]">
        {value}
      </p>
      {detail && (
        <p className="mt-1 text-xs text-[rgb(var(--app-muted))]">{detail}</p>
      )}
    </div>
  );
}

function ChartCard({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="app-panel rounded-[24px] border border-[rgb(var(--app-border)/0.65)] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[rgb(var(--app-text))]">
            {title}
          </h3>
          <p className="mt-1 text-sm text-[rgb(var(--app-muted))]">
            {description}
          </p>
        </div>
        {actions}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const EmptyChart = ({ label }: { label: string }) => (
  <div className="flex h-[280px] items-center justify-center rounded-2xl border border-dashed border-[rgb(var(--app-border)/0.75)] text-sm text-[rgb(var(--app-muted))]">
    {label}
  </div>
);

export default function FinancialsTab() {
  const { theme } = useTheme();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trendRange, setTrendRange] = useState<3 | 6 | 12>(12);
  const requestControllerRef = useRef<AbortController | null>(null);

  const loadSummary = useCallback(async () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const timeout = window.setTimeout(
      () => controller.abort(),
      BILLING_REQUEST_TIMEOUT_MS,
    );
    setLoading(true);
    setError('');
    try {
      const response = await api.get(apiEndpoints.getAdminBillingSummary(), {
        signal: controller.signal,
      });
      if (!response?.ok) {
        throw new Error('The financials dashboard could not be loaded.');
      }
      const data = (await response.json()) as BillingSummary;
      setSummary(data && typeof data === 'object' ? data : {});
    } catch (loadError) {
      if (requestControllerRef.current !== controller) return;
      console.error('Failed to load admin financials dashboard:', loadError);
      setError(
        loadError instanceof DOMException && loadError.name === 'AbortError'
          ? 'The financials request timed out. Please try again.'
          : loadError instanceof Error
            ? loadError.message
            : 'The financials dashboard could not be loaded.',
      );
    } finally {
      window.clearTimeout(timeout);
      if (requestControllerRef.current === controller) {
        requestControllerRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadSummary();
    return () => {
      requestControllerRef.current?.abort();
      requestControllerRef.current = null;
    };
  }, [loadSummary]);

  const statusCounts = summary?.status_counts ?? {};
  const activeCount = numberValue(statusCounts.ACTIVE);
  const pastDueCount = numberValue(statusCounts.PAST_DUE);
  const currentTrend = summary?.last_12_months?.at(-1);
  const lastMonthTrend = summary?.last_12_months?.at(-2);
  const lastMonthLabel = (() => {
    if (!lastMonthTrend?.month) return 'last month';
    const date = new Date(`${lastMonthTrend.month}-01T00:00:00`);
    return Number.isNaN(date.getTime())
      ? lastMonthTrend.month
      : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  })();
  const currentTotalCost =
    summary?.current?.total_cost_cents ??
    numberValue(summary?.current?.retell_cost_cents) +
      numberValue(summary?.current?.opentable_cost_cents);

  const commonChartOptions: ApexOptions = {
    chart: {
      fontFamily: 'Outfit, sans-serif',
      foreColor: theme === 'dark' ? '#98A2B3' : '#667085',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
    },
    theme: { mode: theme },
    dataLabels: { enabled: false },
    grid: { borderColor: theme === 'dark' ? '#344054' : '#EAECF0' },
    legend: { position: 'bottom', fontSize: '12px' },
    tooltip: { theme },
  };

  const fullTrend = Array.isArray(summary?.last_12_months)
    ? summary.last_12_months
    : [];
  const trend = fullTrend.slice(-trendRange);
  const trendOptions: ApexOptions = {
    ...commonChartOptions,
    chart: {
      ...commonChartOptions.chart,
      type: 'line',
      stacked: true,
      stackOnlyBar: true,
    },
    colors: ['#465FFF', '#12B76A', '#F79009', '#7A5AF8'],
    stroke: { width: [0, 0, 0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
    xaxis: {
      categories: trend.map((item) => {
        if (!item.month) return 'Unknown';
        const date = new Date(`${item.month}-01T00:00:00`);
        return Number.isNaN(date.getTime())
          ? item.month
          : date.toLocaleDateString('en-US', {
              month: 'short',
              year: '2-digit',
            });
      }),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: [
      {
        seriesName: ['Paid revenue', 'Retell cost', 'OpenTable cost'],
        labels: { formatter: formatCompactUsd },
        title: { text: 'USD' },
      },
      {
        seriesName: 'Margin %',
        opposite: true,
        min: -100,
        max: 100,
        labels: { formatter: (value) => formatPercent(value) },
        title: { text: 'Margin %' },
      },
    ],
    tooltip: {
      theme,
      y: {
        formatter: (value, options) =>
          options.seriesIndex === 3
            ? formatPercent(value)
            : new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
              }).format(numberValue(value)),
      },
    },
  };
  const trendSeries = [
    {
      name: 'Paid revenue',
      type: 'column',
      group: 'revenue',
      data: trend.map((item) => numberValue(item.paid_revenue_cents) / 100),
    },
    {
      name: 'Retell cost',
      type: 'column',
      group: 'costs',
      data: trend.map((item) => numberValue(item.retell_cost_cents) / 100),
    },
    {
      name: 'OpenTable cost',
      type: 'column',
      group: 'costs',
      data: trend.map((item) => numberValue(item.opentable_cost_cents) / 100),
    },
    {
      name: 'Margin %',
      type: 'line',
      data: trend.map((item) =>
        item.margin_percent == null ? null : item.margin_percent,
      ),
    },
  ];

  const planMix = (summary?.plan_mix ?? []).filter(
    (item) => numberValue(item.mrr_cents) > 0,
  );
  const donutOptions = (
    labels: string[],
    currencyValues = false,
  ): ApexOptions => ({
    ...commonChartOptions,
    labels,
    colors: ['#465FFF', '#12B76A', '#F79009', '#7A5AF8', '#06AED4', '#F04438'],
    stroke: { colors: [theme === 'dark' ? '#101828' : '#FFFFFF'] },
    plotOptions: {
      pie: {
        donut: {
          size: '67%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: (chart) => {
                const total = chart.globals.seriesTotals.reduce(
                  (sum: number, value: number) => sum + value,
                  0,
                );
                return currencyValues
                  ? formatMoney(total, 'USD')
                  : total.toLocaleString();
              },
            },
          },
        },
      },
    },
    tooltip: {
      theme,
      y: {
        formatter: (value) =>
          currencyValues ? formatMoney(value, 'USD') : value.toLocaleString(),
      },
    },
  });

  const statusEntries = Object.entries(statusCounts).filter(
    ([, count]) => numberValue(count) > 0,
  );

  if (loading) {
    return (
      <div className="app-panel flex min-h-[420px] items-center justify-center rounded-[28px]">
        <div className="flex items-center gap-3 text-sm font-medium text-[rgb(var(--app-muted))]">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-[rgb(var(--app-border))] border-t-[rgb(var(--app-brand-strong))]" />
          Loading financials…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-panel rounded-[28px] p-8 text-center">
        <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
        <button
          type="button"
          onClick={() => void loadSummary()}
          className="mt-4 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[rgb(var(--app-text))]">
          Financials
        </h2>
        <p className="mt-1 text-sm text-[rgb(var(--app-muted))]">
          Recurring revenue, service costs, and margin across the business.
          Summary amounts are shown in USD.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="MRR (USD)" value={formatMoney(summary?.mrr_cents)} />
        <KpiCard label="ARR (USD)" value={formatMoney(summary?.arr_cents)} />
        <KpiCard
          label="Active subscriptions"
          value={activeCount.toLocaleString()}
        />
        <KpiCard label="ARPU (USD)" value={formatMoney(summary?.arpu_cents)} />
        <KpiCard
          label="Past due"
          value={pastDueCount.toLocaleString()}
          detail={`${formatMoney(summary?.outstanding_invoice_cents)} outstanding`}
        />
        <KpiCard
          label="Revenue this month (USD)"
          value={formatMoney(currentTrend?.paid_revenue_cents)}
          detail="Paid invoice revenue"
        />
        <KpiCard
          label="Total cost this month (USD)"
          value={formatMoney(currentTotalCost)}
          detail={`${formatMoney(summary?.current?.retell_cost_cents)} Retell + ${formatMoney(
            summary?.current?.opentable_cost_cents,
          )} OpenTable`}
        />
        <KpiCard
          label="Gross margin (last month)"
          value={formatPercent(lastMonthTrend?.margin_percent)}
          detail={`${lastMonthLabel}: ${formatMoney(
            lastMonthTrend?.paid_revenue_cents,
          )} revenue less ${formatMoney(lastMonthTrend?.total_cost_cents)} cost`}
        />
      </div>

      <ChartCard
        title="Revenue, costs, and gross margin"
        description={`Paid revenue and service costs over the last ${trendRange} months (USD).`}
        actions={
          <div className="flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
            {([3, 6, 12] as const).map((months) => (
              <button
                key={months}
                type="button"
                onClick={() => setTrendRange(months)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                  trendRange === months
                    ? 'bg-brand-600 text-white shadow-theme-xs'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {months}M
              </button>
            ))}
          </div>
        }
      >
        {trend.length ? (
          <Chart
            options={trendOptions}
            series={trendSeries}
            type="line"
            height={340}
          />
        ) : (
          <EmptyChart label="No monthly financial history is available." />
        )}
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="MRR by plan"
          description="Recurring monthly value by active plan (USD)."
        >
          {planMix.length ? (
            <Chart
              options={donutOptions(
                planMix.map(
                  (item) => item.plan_name || item.plan_code || 'Unknown',
                ),
                true,
              )}
              series={planMix.map((item) => numberValue(item.mrr_cents))}
              type="donut"
              height={300}
            />
          ) : (
            <EmptyChart label="No recurring plan revenue is available." />
          )}
        </ChartCard>
        <ChartCard
          title="Subscription status"
          description="Current subscriptions by billing status."
        >
          {statusEntries.length ? (
            <Chart
              options={donutOptions(
                statusEntries.map(([status]) => titleCase(status)),
              )}
              series={statusEntries.map(([, count]) => numberValue(count))}
              type="donut"
              height={300}
            />
          ) : (
            <EmptyChart label="No subscription status data is available." />
          )}
        </ChartCard>
      </div>
    </div>
  );
}
